import {
    BarChart as BarChartIcon,
    ViewModule as SessionEngagementIcon,
    AssignmentTurnedIn as SurveyIcon,
    Diversity3 as TeamReportIcon,
} from '@mui/icons-material';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    TextField,
    Typography,
} from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { FadedPaper } from '../theme/StyledFields';
import {
    PRESET_SESSION_ENGAGEMENT,
    PRESET_TEAM,
    REPORT_VIZ_CATALOG,
    ReportVizId,
    ReportVizOption,
    parseVizQuery,
    serializeVizQuery,
} from './reports/catalog';
import ReportSectionHost from './reports/ReportSectionHost';

type PresetChip = {
    label: string;
    description: string;
    to: string;
    icon: React.ElementType;
    vizIds: ReportVizId[];
};

const PRESETS: PresetChip[] = [
    {
        label: 'Team report',
        description:
            'Domain rankings, score variance, tier segmentation, correlations, and week-over-week growth.',
        to: `/reports?v=${serializeVizQuery(PRESET_TEAM)}&week=12`,
        icon: TeamReportIcon,
        vizIds: PRESET_TEAM,
    },
    {
        label: 'Session engagement',
        description:
            'Pre-assessment completion rates and how many athletes have finished enough sessions.',
        to: `/reports?v=${serializeVizQuery(PRESET_SESSION_ENGAGEMENT)}`,
        icon: SessionEngagementIcon,
        vizIds: PRESET_SESSION_ENGAGEMENT,
    },
    {
        label: 'Observer Surveys',
        description:
            'Observer Survey submission volume and completion across your organizations and teams.',
        to: '/reports?v=survey-submissions',
        icon: SurveyIcon,
        vizIds: ['survey-submissions'],
    },
];

function sameVizSelection(a: ReportVizId[], b: ReportVizId[]): boolean {
    if (a.length !== b.length) return false;
    const setB = new Set(b);
    return a.every((id) => setB.has(id));
}

const ReportsHomePage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedIds = useMemo(
        () => parseVizQuery(searchParams.get('v')),
        [searchParams],
    );

    const selectedOptions = useMemo(
        () => REPORT_VIZ_CATALOG.filter((o) => selectedIds.includes(o.id)),
        [selectedIds],
    );

    const [sectionData, setSectionData] = useState<Partial<Record<ReportVizId, unknown>>>({});

    const handleData = useCallback((vizId: ReportVizId, data: unknown | null) => {
        setSectionData((prev) => {
            if (data == null) {
                if (!(vizId in prev)) return prev;
                const next = { ...prev };
                delete next[vizId];
                return next;
            }
            if (prev[vizId] === data) return prev;
            return { ...prev, [vizId]: data };
        });
    }, []);

    const setSelection = (options: ReportVizOption[]) => {
        const ids = options.map((o) => o.id);
        const next = new URLSearchParams(searchParams);
        const serialized = serializeVizQuery(ids);
        if (serialized) {
            next.set('v', serialized);
        } else {
            next.delete('v');
        }
        if (!ids.includes('growth')) {
            next.delete('week');
        } else if (!next.get('week')) {
            next.set('week', '12');
        }
        setSearchParams(next, { replace: true });
    };

    return (
        <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 2, md: 3 } }}>
            <FadedPaper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <BarChartIcon color="primary" sx={{ fontSize: 36 }} />
                    <Typography
                        component={RouterLink} to="/reports"
                        variant="h4" sx={{ fontWeight: 700 }}>
                        Reports
                    </Typography>
                </Box>

                {selectedIds.length === 0 ? (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                            gap: 2,
                            mb: 2,
                            mt: 5
                        }}
                    >
                        {PRESETS.map(({ label, description, to, icon: Icon }) => (
                            <Button
                                key={to}
                                component={RouterLink}
                                to={to}
                                variant="outlined"
                                color="secondary"
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    textAlign: 'left',
                                    gap: 1,
                                    p: 2.5,
                                    height: '100%',
                                    textTransform: 'none',
                                    borderWidth: 1
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Icon sx={{ fontSize: 28 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                        {label}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
                                    {description}
                                </Typography>
                            </Button>
                        ))}
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'nowrap', justifyContent: 'space-between', gap: 1, mb: 2 }}>
                        {PRESETS.map(({ label, to, icon: Icon, vizIds }) => {
                            const active = sameVizSelection(selectedIds, vizIds);
                            return (
                                <Button
                                    key={to}
                                    fullWidth
                                    component={RouterLink}
                                    to={to}
                                    size="small"
                                    variant={active ? 'contained' : 'outlined'}
                                    color="secondary"
                                    aria-current={active ? 'page' : undefined}
                                    startIcon={<Icon />}
                                >
                                    {label}
                                </Button>
                            );
                        })}
                    </Box>
                )}

                <Autocomplete
                    multiple
                    options={REPORT_VIZ_CATALOG}
                    groupBy={(option) => option.group}
                    getOptionLabel={(option) => option.label}
                    value={selectedOptions}
                    onChange={(_, value) => setSelection(value)}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                {...getTagProps({ index })}
                                key={option.id}
                                label={option.label}
                                size="small"
                            />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={selectedIds.length > 0 ? "" : "or select individual charts"}
                        />
                    )}
                />
            </FadedPaper>

            {selectedIds.map((id) => (
                <ReportSectionHost key={id} vizId={id} onData={handleData} />
            ))}

        </Box>
    );
};

export default ReportsHomePage;
